/**
 * MoneySmart.ro - Core Calculations
 * Supports: Savings Account, Accumulation Plan, Child Future, Credit
 */

// ─── Simple Interest (Cont de Economii) ─────────────────────────────────────
export const calculateSimpleInterest = ({
  initialDeposit = 0,
  monthlyContribution = 0,
  annualInterestRate = 0,
  years = 0,
  taxRate = 0,
  pauseStartYear = 0,
  pauseEndYear = 0,
}) => {
  const r = annualInterestRate / 100;
  const yearlyBreakdown = [];
  let totalContributions = initialDeposit;
  let totalInterestGross = 0;
  let totalTaxPaid = 0;
  let currentBalance = initialDeposit;

  for (let year = 1; year <= years; year++) {
    const yearStartBalance = currentBalance;
    const isPaused = pauseStartYear > 0 && pauseEndYear > 0 &&
      year >= pauseStartYear && year <= pauseEndYear;
    const yearContributions = isPaused ? 0 : monthlyContribution * 12;
    const yearInterestGross = yearStartBalance * r;
    const yearTaxPaid = yearInterestGross * (taxRate / 100);
    const yearInterestNet = yearInterestGross - yearTaxPaid;
    currentBalance = yearStartBalance + yearContributions + yearInterestNet;
    totalContributions += yearContributions;
    totalInterestGross += yearInterestGross;
    totalTaxPaid += yearTaxPaid;

    yearlyBreakdown.push({
      year,
      yearStartBalance: round(yearStartBalance),
      contributions: round(yearContributions),
      totalContributions: round(totalContributions),
      interestGross: round(yearInterestGross),
      taxPaid: round(yearTaxPaid),
      interestNet: round(yearInterestNet),
      adminFee: 0,
      endBalance: round(currentBalance),
      totalInterestGross: round(totalInterestGross),
      totalTaxPaid: round(totalTaxPaid),
      totalAdminFees: 0,
      isPaused,
    });
  }

  const totalInterestNet = totalInterestGross - totalTaxPaid;
  return {
    finalValue: round(currentBalance),
    finalValueNet: round(currentBalance),
    totalContributions: round(totalContributions),
    totalInterestGross: round(totalInterestGross),
    totalInterestNet: round(totalInterestNet),
    totalTaxPaid: round(totalTaxPaid),
    totalAdminFees: 0,
    yearlyBreakdown,
  };
};

const round = (v) => Math.round((v || 0) * 100) / 100;

// ─── Compound Interest (Plan Acumulare / Viitorul Copilului) ─────────────────
export const calculateCompoundInterest = ({
  initialDeposit = 0,
  monthlyContribution = 0,
  annualInterestRate = 0,
  years = 0,
  compoundingFrequency = 12,
  contributionFrequency = 12,
  taxRate = 0,
  taxOnEachPeriod = false,
  adminFeeRate = 0,
  pauseStartYear = 0,
  pauseEndYear = 0,
  useSimpleInterest = false,
  // Top-up: array of { year, amount }
  topUps = [],
  // Withdrawals: array of { year, amount }
  withdrawals = [],
}) => {
  if (useSimpleInterest) {
    return calculateSimpleInterest({
      initialDeposit, monthlyContribution, annualInterestRate,
      years, taxRate, pauseStartYear, pauseEndYear,
    });
  }

  const r = annualInterestRate / 100;
  const n = compoundingFrequency;
  const periodicRate = r / n;
  const adminFee = adminFeeRate / 100;
  const annualContribution = monthlyContribution * 12;
  const contributionPerPeriod = annualContribution / n;

  const yearlyBreakdown = [];
  let currentBalance = initialDeposit;
  let totalContributions = initialDeposit;
  let totalInterestGross = 0;
  let totalTaxPaid = 0;
  let totalAdminFees = 0;
  let totalTopUps = 0;
  let totalWithdrawals = 0;

  for (let year = 1; year <= years; year++) {
    const yearStartBalance = currentBalance;
    let yearContributions = 0;
    let yearInterestGross = 0;
    let yearTaxPaid = 0;
    let yearAdminFee = 0;

    const isPaused = pauseStartYear > 0 && pauseEndYear > 0 &&
      year >= pauseStartYear && year <= pauseEndYear;

    // Top-up for this year
    const yearTopUp = (topUps || [])
      .filter(t => t.year === year)
      .reduce((sum, t) => sum + (t.amount || 0), 0);
    if (yearTopUp > 0) {
      currentBalance += yearTopUp;
      totalTopUps += yearTopUp;
      totalContributions += yearTopUp;
    }

    for (let period = 1; period <= n; period++) {
      const periodInterest = currentBalance * periodicRate;
      yearInterestGross += periodInterest;
      if (taxOnEachPeriod && taxRate > 0) {
        const periodTax = periodInterest * (taxRate / 100);
        yearTaxPaid += periodTax;
        currentBalance += periodInterest - periodTax;
      } else {
        currentBalance += periodInterest;
      }
      if (!isPaused) {
        currentBalance += contributionPerPeriod;
        yearContributions += contributionPerPeriod;
      }
    }

    // Admin fee
    if (adminFee > 0) {
      yearAdminFee = currentBalance * adminFee;
      currentBalance -= yearAdminFee;
      totalAdminFees += yearAdminFee;
    }

    // Withdrawal for this year
    const yearWithdrawal = (withdrawals || [])
      .filter(w => w.year === year)
      .reduce((sum, w) => sum + (w.amount || 0), 0);
    if (yearWithdrawal > 0 && currentBalance >= yearWithdrawal) {
      currentBalance -= yearWithdrawal;
      totalWithdrawals += yearWithdrawal;
    }

    if (!taxOnEachPeriod && taxRate > 0) {
      yearTaxPaid = yearInterestGross * (taxRate / 100);
    }

    totalContributions += yearContributions;
    totalInterestGross += yearInterestGross;
    totalTaxPaid += yearTaxPaid;

    yearlyBreakdown.push({
      year,
      yearStartBalance: round(yearStartBalance),
      contributions: round(yearContributions),
      totalContributions: round(totalContributions),
      interestGross: round(yearInterestGross),
      taxPaid: round(yearTaxPaid),
      interestNet: round(yearInterestGross - yearTaxPaid),
      adminFee: round(yearAdminFee),
      topUp: round(yearTopUp),
      withdrawal: round(yearWithdrawal),
      endBalance: round(currentBalance),
      totalInterestGross: round(totalInterestGross),
      totalTaxPaid: round(totalTaxPaid),
      totalAdminFees: round(totalAdminFees),
      isPaused,
    });
  }

  let netFinalValue = currentBalance;
  if (!taxOnEachPeriod && taxRate > 0) {
    const totalTax = totalInterestGross * (taxRate / 100);
    netFinalValue = currentBalance - totalTax;
    totalTaxPaid = totalTax;
  }

  return {
    finalValue: round(currentBalance),
    finalValueNet: round(netFinalValue),
    totalContributions: round(totalContributions),
    totalInterestGross: round(totalInterestGross),
    totalInterestNet: round(totalInterestGross - totalTaxPaid),
    totalTaxPaid: round(totalTaxPaid),
    totalAdminFees: round(totalAdminFees),
    totalTopUps: round(totalTopUps),
    totalWithdrawals: round(totalWithdrawals),
    yearlyBreakdown,
  };
};

// ─── Credit Calculator (Anuități) ──────────────────────────────────────────
export const calculateCredit = ({
  loanAmount = 0,
  annualInterestRate = 0,
  termMonths = 0,
  monthlyFeeRate = 0,     // comision lunar %
  gracePeriodMonths = 0,  // perioadă de grație
  repaymentType = 'annuity', // 'annuity' | 'principal'
  extraMonthlyPayment = 0, // plată anticipată suplimentară
}) => {
  if (loanAmount <= 0 || termMonths <= 0) {
    return { monthlyPayment: 0, totalPayment: 0, totalInterest: 0, monthlyBreakdown: [] };
  }

  const monthlyRate = annualInterestRate / 100 / 12;
  const monthlyFee = loanAmount * (monthlyFeeRate / 100);

  // Calculate standard annuity payment
  let monthlyPayment = 0;
  const effectiveMonths = termMonths - gracePeriodMonths;

  if (monthlyRate === 0) {
    monthlyPayment = loanAmount / effectiveMonths;
  } else if (repaymentType === 'annuity') {
    monthlyPayment = (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, effectiveMonths)) /
      (Math.pow(1 + monthlyRate, effectiveMonths) - 1);
  }

  const monthlyBreakdown = [];
  let balance = loanAmount;
  let totalInterest = 0;
  let totalPrincipal = 0;
  let totalFees = 0;
  let actualTermMonths = 0;

  for (let month = 1; month <= termMonths + 120; month++) {
    if (balance <= 0.01) break;
    actualTermMonths = month;

    const interest = balance * monthlyRate;
    let principal = 0;
    let payment = 0;

    if (month <= gracePeriodMonths) {
      // Grace period: pay only interest
      principal = 0;
      payment = interest + monthlyFee;
    } else if (repaymentType === 'annuity') {
      principal = Math.min(monthlyPayment - interest + extraMonthlyPayment, balance);
      payment = interest + principal + monthlyFee;
    } else {
      // Equal principal
      const stdPrincipal = loanAmount / effectiveMonths;
      principal = Math.min(stdPrincipal + extraMonthlyPayment, balance);
      payment = interest + principal + monthlyFee;
    }

    balance = Math.max(0, balance - principal);
    totalInterest += interest;
    totalPrincipal += principal;
    totalFees += monthlyFee;

    monthlyBreakdown.push({
      month,
      payment: round(payment),
      principal: round(principal),
      interest: round(interest),
      fee: round(monthlyFee),
      balance: round(balance),
      totalInterest: round(totalInterest),
      totalPrincipal: round(totalPrincipal),
    });
  }

  const totalPayment = totalPrincipal + totalInterest + totalFees;
  const effectiveRate = loanAmount > 0 ? (totalInterest / loanAmount) * 100 : 0;

  return {
    monthlyPayment: round(repaymentType === 'annuity' ? monthlyPayment + monthlyFee : (loanAmount / effectiveMonths) + monthlyFee),
    totalPayment: round(totalPayment),
    totalInterest: round(totalInterest),
    totalFees: round(totalFees),
    actualTermMonths,
    effectiveRate: round(effectiveRate),
    monthlyBreakdown,
  };
};

// ─── Compare Strategies ─────────────────────────────────────────────────────
export const compareStrategies = (params1, params2) => {
  const r1 = calculateCompoundInterest(params1);
  const r2 = calculateCompoundInterest(params2);
  return {
    strategy1: r1,
    strategy2: r2,
    difference: {
      finalValue: r1.finalValue - r2.finalValue,
      totalInterestNet: r1.totalInterestNet - r2.totalInterestNet,
      totalContributions: r1.totalContributions - r2.totalContributions,
    },
  };
};

// ─── Chart Data ─────────────────────────────────────────────────────────────
export const generateChartData = (yearlyBreakdown) => {
  return yearlyBreakdown.map((year) => ({
    year: year.year,
    contributions: year.totalContributions,
    interest: year.totalInterestGross - year.totalTaxPaid,
    total: year.endBalance,
    interestGross: year.totalInterestGross,
    taxPaid: year.totalTaxPaid,
    isPaused: year.isPaused,
  }));
};
